import { AllocationDimensionKind, IAllocationDimension } from "helpers/allocation-dimension";
import { CustomComponentType, OperatorType } from "components/common/filters-query-builder/constants";
import memoize from "memoize-one";
import { RuleType } from "react-querybuilder";
import { ISchemaKey } from "helpers/product-collection";
import {
    SR_CUSTOM_DOOR_GROUPING_DELIMITER,
    getDoorAttributeWithAllowedValuesWithBlankValue,
} from "helpers/door-collection";
import { CollectionRefs } from "components/product-collection/product-collection-views/product-collection-grid-view/tool-panels/dimensions-tool-panel/dimensions-tool-panel";
import { IDimensionOption, IDimensionFilter, IAttributeFilters } from "./types";
import { formatMessage } from "lib/globalize-util";
import { CustomUserDefinedAttributeOption } from "./current/custom-user-defined-attribute-option";
import { ATTRIBUTES } from "./constants";

export const buildDimensionOptions = memoize(
    ({
        allocationDimensions,
        allocationDimensionAttributes = [],
        dimensionFilters,
        isLegacy,
        collectionsRefs,
        isServerRollup,
    }: {
        allocationDimensions: IAllocationDimension[];
        allocationDimensionAttributes?: ISchemaKey[];
        dimensionFilters: IDimensionFilter[];
        isLegacy?: boolean;
        collectionsRefs?: CollectionRefs;
        isServerRollup?: boolean;
    }): IDimensionOption[] => {
        const dimensionOptions: IDimensionOption[] = allocationDimensions
            .filter((dimension) => dimension.subtype === AllocationDimensionKind.Generic)
            .map((dimension, index) => {
                const attributeValues = dimension.dimension_values.reduce((acc, item) => {
                    if (item.included) {
                        acc.push({
                            name: item.id,
                            label: item.name,
                            value: item.id,
                            group: item.group,
                            selected: dimensionFilters[index]?.includes(item.id),
                        });
                    }

                    return acc;
                }, []);

                const dimensionOption: IDimensionOption = {
                    id: dimension.id,
                    name: dimension.name,
                    label: dimension.name,
                    placeholder: `${formatMessage("Select")} ${dimension.name}`,
                    values: attributeValues,
                };

                if (!isLegacy) {
                    dimensionOption.name = dimension.id;
                    dimensionOption.operators = [{ name: OperatorType.IN, label: OperatorType.IN }];
                    dimensionOption.customComponentType = CustomComponentType.Selector;
                    dimensionOption.multiple = true;
                    dimensionOption.isUnique = true;
                }
                return dimensionOption;
            });

        if (!isLegacy) {
            const attributesOptions = buildAttributesOptions({
                allocationDimensionAttributes,
                collectionsRefs,
                isServerRollup,
            });
            dimensionOptions.push({
                name: ATTRIBUTES,
                label: formatMessage("Door Attributes"),
                operators: [{ name: OperatorType.IN, label: OperatorType.IN }],
                values: attributesOptions,
                placeholder: formatMessage("Select attributes"),
                customComponentType: CustomComponentType.DependantSelector,
                isUnique: false,
                isSearchable: true,
            });
        }
        return dimensionOptions;
    }
);

export const buildAttributesOptions = memoize(
    ({
        allocationDimensionAttributes = [],
        collectionsRefs,
        isServerRollup,
    }: {
        allocationDimensionAttributes: ISchemaKey[];
        collectionsRefs?: CollectionRefs;
        isServerRollup?: boolean;
    }): IDimensionOption[] => {
        return allocationDimensionAttributes
            .filter((allocationDimensionAttribute) => allocationDimensionAttribute?.constraint)
            .map((allocationDimensionAttribute) => {
                const updatedAllocationDimensionAttribute = getDoorAttributeWithAllowedValuesWithBlankValue({
                    allocationDimensionAttribute: allocationDimensionAttribute,
                    blankAtBeginning: true,
                });
                const attributeValues = updatedAllocationDimensionAttribute.constraint?.allowed_values?.map((item) => ({
                    name: item.string_value,
                    label: item.string_value || `[${formatMessage("Blank")}]`,
                    value: item.string_value,
                    selected: false,
                }));
                const collectionName = updatedAllocationDimensionAttribute.collectionId
                    ? collectionsRefs?.[updatedAllocationDimensionAttribute.collectionId]
                    : "";
                const isUserDefined = updatedAllocationDimensionAttribute.display?.is_user_defined;

                const dimensionOption: IDimensionOption = {
                    name: buildAttributesId(updatedAllocationDimensionAttribute),
                    label: updatedAllocationDimensionAttribute.display_name,
                    operators: [{ name: OperatorType.IN, label: OperatorType.IN }],
                    values: attributeValues,
                    collectionName,
                    isUserDefined,
                    multiple: true,
                    isUnique: true,
                };

                if (isUserDefined) {
                    dimensionOption.onRenderCustomOption = (option) => {
                        return CustomUserDefinedAttributeOption({
                            option,
                            collectionName,
                            isServerRollup,
                            isUserDefined,
                        });
                    };
                }
                return dimensionOption;
            })
            .sort((A, B) => {
                if (A.isUserDefined && !B.isUserDefined) {
                    return 1;
                } else if (!A.isUserDefined && B.isUserDefined) {
                    return -1;
                } else {
                    const nameCompared = A.label?.localeCompare(B.label);
                    if (nameCompared === 0) {
                        return A.collectionName?.localeCompare(B.collectionName);
                    } else {
                        return nameCompared;
                    }
                }
            });
    }
);

export const transformQueryBuilderRulesToDimensionFilters = memoize(
    (allocationDimensions: IAllocationDimension[], rules: RuleType[]): IDimensionFilter[] => {
        const rulesObj = rules.reduce((result, rule: RuleType) => {
            result[rule.field] = rule.value;
            return result;
        }, {});
        // Create dimension's structure with 2 dimensions array [[], []]
        const newDimensionFilters: IDimensionFilter[] = [];
        allocationDimensions.forEach((dimension, index) => {
            newDimensionFilters[index] = rulesObj[dimension.id] ?? [];
        });

        return newDimensionFilters;
    }
);

export const transformQueryBuilderRulesToAttributeFilters = memoize((rules: RuleType[]): IAttributeFilters => {
    return rules.reduce((result: IAttributeFilters, rule: RuleType) => {
        result[rule.value.parent] = rule.value.children;
        return result;
    }, {});
});

export const transformDimensionFiltersToQueryBuilderRules = memoize(
    (allocationDimensions: IAllocationDimension[], dimensionFilters: IDimensionFilter[]): RuleType[] => {
        return dimensionFilters
            .reduce((result: RuleType[], value: IDimensionFilter, index) => {
                result[index] = {
                    id: allocationDimensions[index].id,
                    field: allocationDimensions[index].id,
                    value,
                    operator: OperatorType.IN,
                };
                return result;
            }, [])
            .filter((rule) => !!rule.value?.length);
    }
);

export const transformAttributeFiltersToQueryBuilderRules = memoize(
    (attributeFilters: IAttributeFilters): RuleType[] => {
        return Object.keys(attributeFilters).reduce((result: RuleType[], key: string, index) => {
            result[index] = {
                id: `${ATTRIBUTES}_${index}`,
                field: ATTRIBUTES,
                value: {
                    parent: key,
                    children: attributeFilters[key],
                },
                operator: OperatorType.IN,
            };
            return result;
        }, []);
    }
);

/**
 * Handles building the attributesId. It checks if collectionId is presents
 * If so the attribute we're in a rollup so we concatenate the collectionId
 * with the attributes name to avoid duplicated Ids
 */
export const buildAttributesId = (dimensionAttribute: ISchemaKey) =>
    dimensionAttribute.collectionId
        ? `${dimensionAttribute.field_name}${SR_CUSTOM_DOOR_GROUPING_DELIMITER}${dimensionAttribute.collectionId}`
        : dimensionAttribute.field_name;
